"use client";

import React, { useEffect, useRef } from "react";
import { useTerminalStore } from "@/lib/terminal/store";
import type { TerminalEntry } from "@/lib/terminal/types";
import { SkeletonRows } from "@/components/common/SkeletonRows";
import { AlertTriangle } from "lucide-react";

const OUTPUT_SKELETON = { columnWidths: [120, 200], rowCount: 3 } as const;

/** Pixels from the bottom within which we still consider the user "at bottom". */
const AUTO_SCROLL_THRESHOLD_PX = 40;

const EntryRow = React.memo(function EntryRow({ entry }: { entry: TerminalEntry }) {
  const time = new Date(entry.createdAt).toLocaleTimeString();
  return (
    <div className="border-b border-border/40 px-3 py-2 text-xs font-mono">
      <div className="flex items-baseline gap-2 text-muted-foreground">
        <span>{time}</span>
        <span className="text-foreground">&gt;</span>
        <span className="text-foreground/90 break-all">{entry.input}</span>
        {entry.durationMs !== undefined && (
          <span className="ml-auto text-[10px] text-muted-foreground">
            {entry.durationMs.toFixed(1)}ms
          </span>
        )}
      </div>
      {entry.status === "pending" && (
        <div className="mt-1" aria-label="Running command…">
          <SkeletonRows spec={OUTPUT_SKELETON} />
        </div>
      )}
      {entry.status === "ok" && (
        <pre className="mt-1 whitespace-pre-wrap break-all text-foreground/90">
          {entry.output}
        </pre>
      )}
      {entry.status === "error" && entry.error && (
        <div className="mt-1 flex flex-col gap-1">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[9.5px] font-mono font-bold uppercase tracking-wide text-destructive">
            <AlertTriangle className="h-3 w-3" />
            {entry.error.code}
          </span>
          <pre className="whitespace-pre-wrap break-all text-destructive">
            {entry.error.message}
            {entry.error.offset !== undefined
              ? `\n  at offset ${entry.error.offset}`
              : ""}
          </pre>
        </div>
      )}
    </div>
  );
});

export default function TerminalOutput() {
  const entries = useTerminalStore((s) => s.entries);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // Whether the user was at/near the bottom before the latest update.
  const stickToBottomRef = useRef(true);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <=
      AUTO_SCROLL_THRESHOLD_PX;
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // Only auto-scroll when the user was already at/near the bottom;
    // never yank them down while they're reading scrollback.
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [entries]);

  return (
    <div
      ref={scrollerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto bg-background"
      data-testid="terminal-output"
    >
      {entries.length === 0 ? (
        <pre className="whitespace-pre-wrap px-3 py-2 text-xs font-mono text-muted-foreground">
{`VybeTrader Terminal — type a DSL expression and press Enter.

Type help or Help() for available commands.

Examples:
  help
  Watchlist.Add("EURUSD")
  Watchlist.Remove("EURUSD")
  Watchlist.Symbol.Price("EURUSD")`}
        </pre>
      ) : (
        entries.map((entry) => <EntryRow key={entry.id} entry={entry} />)
      )}
    </div>
  );
}
